import React, { useEffect, useMemo, useRef } from "react";
import { Wallet } from "lucide-react";

import { latestPayProfile, resolveActiveEmployee, money } from "../../Shared/ScreenUtils.jsx";

export function PayslipsScreen({ data, reload }) {
  const me = data.me || {};
  const employee = resolveActiveEmployee(data) || {};
  const myId = employee.id;

  // Same reason as the Employee Register: pay is loaded once at boot and never
  // polled, so somebody who got a raise today would open this page in an
  // already-open tab and still see their old salary.
  const refreshedOnOpen = useRef(false);
  useEffect(() => {
    if (refreshedOnOpen.current || !reload) return;
    refreshedOnOpen.current = true;
    reload(["payProfiles", "paymentSnapshots"]);
  }, [reload]);

  const payProfile = useMemo(
    () => latestPayProfile(data.payProfiles, myId),
    [data.payProfiles, myId]);

  const snapshots = useMemo(() => {
    return (data.paymentSnapshots || [])
      .filter(s => String(s.employee) === String(myId))
      .sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month));
  }, [data.paymentSnapshots, myId]);

  const MONTH_NAMES = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const basePay = Number(payProfile?.base_pay || 0);
  const payType = payProfile?.pay_type || "Not Set";
  const payPerTask = Number(payProfile?.pay_per_task || 0);

  return (
    <section style={{ padding: "24px 32px", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 24px" }}>My Payslips</h1>

      {/* Pay Summary Card */}
      <div style={{ background: "var(--surface-soft)", border: "1px solid var(--c-200)", borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--blue-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Wallet size={20} color="var(--blue-700)" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{employee.display_name || "—"}</h2>
            <p style={{ margin: 0, fontSize: 12, color: "var(--c-500)" }}>{employee.department_name || "—"} · {employee.position_title || "—"}</p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <PayCard label="Base Pay" value={`₹${money(basePay)}`} />
          <PayCard label="Pay Type" value={payType} />
          <PayCard label="Pay Per Task" value={payPerTask ? `₹${money(payPerTask)}` : "—"} />
          <PayCard label="Employment" value={employee.employment_type || "—"} />
        </div>
      </div>

      {/* Payment History */}
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 12px" }}>Payment History</h3>

      {snapshots.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)", border: "1px solid var(--c-200)", borderRadius: 10 }}>
          No Payment Records Found Yet.
        </div>
      ) : (
        <div style={{ border: "1px solid var(--c-200)", borderRadius: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
            <thead>
              <tr style={{ background: "var(--surface-soft)" }}>
                <th style={TH}>Month</th>
                <th style={{ ...TH, textAlign: "right" }}>Base Pay</th>
                <th style={{ ...TH, textAlign: "right" }}>Bonus</th>
                <th style={{ ...TH, textAlign: "right" }}>Bounty</th>
                <th style={{ ...TH, textAlign: "right" }}>Deduction</th>
                <th style={{ ...TH, textAlign: "right" }}>Net Pay</th>
                <th style={{ ...TH, textAlign: "center" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(snap => {
                const net = Number(snap.normal_pay || 0) + Number(snap.bonus || 0) + Number(snap.bounty || 0) - Number(snap.deduction || 0);
                const status = snap.finance_status || snap.manager_status || "Pending";
                return (
                  <tr key={snap.id} style={{ borderBottom: "1px solid var(--c-100)" }}>
                    <td style={TD}><strong>{MONTH_NAMES[snap.month]} {snap.year}</strong></td>
                    <td style={{ ...TD, textAlign: "right" }}>₹{money(snap.normal_pay)}</td>
                    <td style={{ ...TD, textAlign: "right" }}>₹{money(snap.bonus)}</td>
                    <td style={{ ...TD, textAlign: "right" }}>₹{money(snap.bounty)}</td>
                    <td style={{ ...TD, textAlign: "right", color: Number(snap.deduction) > 0 ? "var(--red-600)" : undefined }}>{Number(snap.deduction) > 0 ? `-₹${money(snap.deduction)}` : "₹0"}</td>
                    <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: "var(--green-600)" }}>₹{money(net)}</td>
                    <td style={{ ...TD, textAlign: "center" }}>
                      <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, fontWeight: 500, background: status === "Approved" || status === "Paid" ? "var(--green-100)" : status === "Batched" ? "var(--amber-50)" : "var(--c-100)", color: status === "Approved" || status === "Paid" ? "var(--green-700)" : status === "Batched" ? "var(--amber-600)" : "var(--c-500)" }}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const TH = { padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "var(--c-600)", borderBottom: "1px Solid var(--c-200)", whiteSpace: "nowrap" };
const TD = { padding: "10px 14px", whiteSpace: "nowrap" };

function PayCard({ label, value }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, color: "var(--c-500)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--c-900)" }}>{value}</div>
    </div>
  );
}
